import { useEffect, useRef } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { vehicleIcon } from "@/components/map/markers";
import { vehicleStatus } from "@/lib/fleet";

const STYLE_URL = "https://tiles.openfreemap.org/styles/liberty";

function popupHtml(v) {
  const esc = (value) =>
    String(value ?? "-").replace(/[&<>"']/g, (c) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]));

  return `<div style="min-width:180px;color:#111">
    <strong>${esc(v.name || "Kendaraan")}</strong>
    <div style="font-family:monospace;margin:5px 0">${esc(v.plate)}</div>
    <div>Kecepatan: <b>${Math.round(Number(v.speed) || 0)} km/j</b></div>
    <div>ACC: <b>${v.acc === true ? "ON" : v.acc === false ? "OFF" : "BELUM ADA DATA"}</b></div>
    <div>Status: <b>${esc(vehicleStatus(v))}</b></div>
  </div>`;
}

export function FleetMap3D({ vehicles = [], selectedId, onSelect, follow = false }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef(new Map());
  const firstFitRef = useRef(false);
  const latestRef = useRef({ vehicles, selectedId, onSelect, follow });

  latestRef.current = { vehicles, selectedId, onSelect, follow };

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE_URL,
      center: [106.83, -6.2],
      zoom: 12,
      pitch: 55,
      bearing: -15,
      antialias: true,
      attributionControl: true,
    });

    mapRef.current = map;

    map.on("error", (event) => {
      console.error("[Glazy MapLibre]", event?.error || event);
    });

    map.on("load", () => {
      console.info("[Glazy MapLibre] style loaded", {
        loaded: map.isStyleLoaded(),
        canvas: {
          width: map.getCanvas().width,
          height: map.getCanvas().height,
        },
        center: map.getCenter().toArray(),
        zoom: map.getZoom(),
      });
    });
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), "bottom-right");

    map.on("load", () => {
      const layers = map.getStyle()?.layers || [];
      const buildingLayer = layers.find((layer) => layer.id === "building-3d");
      if (buildingLayer && !map.getLayoutProperty(buildingLayer.id, "visibility")) {
        map.setLayoutProperty(buildingLayer.id, "visibility", "visible");
      }
      map.resize();
    });

    const resizeObserver = new ResizeObserver(() => map.resize());
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      markersRef.current.forEach((item) => item.marker.remove());
      markersRef.current.clear();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const currentIds = new Set();

    vehicles.forEach((v) => {
      if (v.lat == null || v.lng == null) return;
      const id = String(v.id);
      currentIds.add(id);

      const icon = vehicleIcon(v, v.id === selectedId);
      let item = markersRef.current.get(id);

      if (!item) {
        const element = document.createElement("div");
        element.className = icon.options.className || "gt-marker-wrap";
        element.innerHTML = icon.options.html;

        const popup = new maplibregl.Popup({ offset: 24, maxWidth: "280px" })
          .setHTML(popupHtml(v));

        const marker = new maplibregl.Marker({
          element,
          anchor: "center",
        })
          .setLngLat([Number(v.lng), Number(v.lat)])
          .setPopup(popup)
          .addTo(map);

        element.addEventListener("click", () => latestRef.current.onSelect?.(v.id));
        item = { marker, element, key: "" };
        markersRef.current.set(id, item);
      } else {
        item.marker.setLngLat([Number(v.lng), Number(v.lat)]);
      }

      const key = `${vehicleStatus(v)}|${v.direction}|${v.id === selectedId}|${v.plate}|${v.speed}|${v.acc}`;
      if (item.key !== key) {
        item.element.innerHTML = icon.options.html;
        item.marker.getPopup()?.setHTML(popupHtml(v));
        item.key = key;
      }
    });

    markersRef.current.forEach((item, id) => {
      if (!currentIds.has(id)) {
        item.marker.remove();
        markersRef.current.delete(id);
      }
    });

    if (!firstFitRef.current) {
      const valid = vehicles.filter((v) => v.lat != null && v.lng != null);
      if (valid.length) {
        if (valid.length === 1) {
          map.setCenter([Number(valid[0].lng), Number(valid[0].lat)]);
          map.setZoom(15);
        } else {
          const bounds = new maplibregl.LngLatBounds();
          valid.forEach((v) => bounds.extend([Number(v.lng), Number(v.lat)]));
          map.fitBounds(bounds, { padding: 60, maxZoom: 15, duration: 0 });
        }
        firstFitRef.current = true;
      }
    }
  }, [vehicles, selectedId]);

  useEffect(() => {
    const map = mapRef.current;
    const selected = vehicles.find((v) => v.id === selectedId);
    if (map && selected?.lat != null && selected?.lng != null) {
      map.easeTo({
        center: [Number(selected.lng), Number(selected.lat)],
        zoom: Math.max(map.getZoom(), 15),
        duration: 700,
      });
    }
  }, [selectedId]);

  useEffect(() => {
    const map = mapRef.current;
    const selected = vehicles.find((v) => v.id === selectedId);
    if (follow && map && selected?.lat != null && selected?.lng != null) {
      map.easeTo({ center: [Number(selected.lng), Number(selected.lat)], duration: 700 });
    }
  }, [follow, selectedId, vehicles]);

  return <div ref={containerRef} className="absolute inset-0 z-0 h-full w-full gt-maplibre-3d" data-testid="fleet-map-3d" />;
}
